import type { BuyerImportPlanOrigin, BuyerSmartImportPreviewChip } from "@/api/buyerSmartImport";
import { formatCalendarDate } from "@/design-system/lib/format";

/**
 * Every sentence this feature says, in one place.
 *
 * <p>Several are named VERBATIM by the rulings — "matched automatically",
 * "using the mapping from…", "Map with AI instead" — and a ruling's wording is
 * not a label somebody may improve in passing. Holding them here means the
 * tests assert the shipped string rather than a paraphrase of it, and a
 * reworded one fails loudly instead of quietly ceasing to be the thing that
 * was approved.</p>
 */
export const smartImportCopy = {
  /** ------------------------------------------------------------------ */
  /** Landing                                                             */
  /** ------------------------------------------------------------------ */

  uploadTitle: "Import buyers",
  uploadDescription:
    "Upload a spreadsheet or paste rows out of one. Phoenix reads the columns, proposes a mapping, and shows you exactly what it will create before anything is written.",

  fileLabel: "Spreadsheet",
  fileHint: "CSV, XLSX, XLSM or XLS, up to 10 MB. Macros are never run — only cell values are read.",
  pasteLabel: "…or paste rows",
  pasteHint:
    "Copy the cells out of a spreadsheet, including the header row, and paste them here.",
  pastePlaceholder: "First name\tLast name\tEmail\tPhone",

  analyzeAction: "Read this file",
  analyzing: "Reading the file…",

  /**
   * D10, stated rather than implied. A reader who leaves both pickers alone
   * has made a choice, and it is not "nothing happens".
   */
  routingNote:
    "Leave both blank and every row follows today's routing — the team its advertisement or its site implies, and the manager's queue for an owner.",
  teamLabel: "Team (optional)",
  teamNone: "No team — use today's routing",
  assignToLabel: "Assign every row to (optional)",
  assignToNone: "Unassigned — leave them in the queue",
  assignToNeedsTeamHint: "Listing this team's members. Clear the team to choose from everyone.",
  assignToAllHint: "Listing everyone who can be assigned a buyer. Choose a team to narrow it.",
  advertisementLabel: "Advertisement (optional)",
  advertisementNone: "None",
  advertisementTeamHint: "Only this team's advertisements are offered.",
  /** D16 — the pick refills every tab's Source from the ad's channel. */
  advertisementSourceNote:
    "Picking an advertisement sets every tab's Source from its channel. A mapped Source column still wins over it.",

  /** ------------------------------------------------------------------ */
  /** Plan provenance                                                      */
  /** ------------------------------------------------------------------ */

  /** The ruling's own words for an exact-match plan. */
  originExactMatch: "matched automatically",
  originAi: "mapped by Phoenix AI",
  originManualFallback: "mapped by hand",
  /** Offered whenever the plan did NOT come from the model. */
  remapWithAi: "Map with AI instead",

  /** ------------------------------------------------------------------ */
  /** Review                                                               */
  /** ------------------------------------------------------------------ */

  reviewTitle: "Review the mapping",
  includeTabLabel: "Import this tab",
  headerRowLabel: "Header row",
  headerRowHint: "Which raw row holds the column names.",
  tabSourceLabel: "Source for this tab",
  tabSourceHint: "A mapped Source column still wins over this.",
  tabSourceDetailLabel: "Source detail for this tab",
  tabSourceDetailHint:
    "Which site, event or list these rows came from. \"Listing site\" on its own does not say which one.",
  tabSourceDetailPlaceholder: "BizBuySell",

  columnHeader: "Column",
  samplesHeader: "Sample values",
  targetHeader: "Maps to",
  transformHeader: "Read as",
  sentinelsHeader: "Treated as blank",
  reasonHeader: "Why",
  noSamples: "No sample values",
  lowConfidence: "Low confidence — check the samples",
  sentinelsPlaceholder: "n/a, not disclosed",
  sentinelsHint: "Comma-separated. Always in addition to the built-in list.",
  formatLabel: "Date format",
  formatHint: "The format these dates are written in, e.g. M/d/yy h:mmtt.",

  interestsTitle: "Interests from the listings",
  interestsSwitch: "Infer interests from the listing each row enquired about",
  interestsHint:
    "Each listing's industries and geographies are added to the buyers who enquired about it. Edit anything that reads wrong.",
  interestsLoading: "Inferring interests…",
  interestsEmpty: "No listings were found in the mapped columns.",

  cutoffLabel: "Only import rows received on or after",
  cutoffHint: "Leave blank to import every row.",
  cutoffClear: "Clear the cutoff",

  /** ------------------------------------------------------------------ */
  /** Counts (D4)                                                          */
  /** ------------------------------------------------------------------ */

  countsTitle: "What this will do",
  rowsRead: "Rows read",
  created: "New buyers",
  merged: "Merged into another row",
  alreadyImported: "Already imported",
  beforeCutoff: "Before the cutoff",
  rejected: "Rejected",
  duplicatesExisting: "Matches an existing buyer",
  duplicatesWithinFile: "Duplicate within this file",
  valuesCleared: "Values cleared",
  valuesClearedHint:
    "Counted only for rows that will import — a value cleared on a rejected row is not a loss anybody can act on.",
  sentinelsFound: "Reported as not provided",
  sentinelsHint2:
    "Cells matching a blank-value marker. Reported as not provided, never as cleared.",
  datesUnreadable: "Kept despite an unreadable date",

  /** ------------------------------------------------------------------ */
  /** Preview (D4)                                                         */
  /** ------------------------------------------------------------------ */

  previewTitle: "Preview",
  previewCap: "At most ten are shown under each heading.",
  previewNotesLabel: "Notes",
  previewInquiries: "inquiries folded in",
  rejectedRowsTitle: "Rejected rows",
  downloadRejected: "Download the rejected rows",

  /** ------------------------------------------------------------------ */
  /** Commit + progress (D20)                                              */
  /** ------------------------------------------------------------------ */

  commitAction: "Import all",
  commitHint: "This commits exactly what the preview above shows.",
  queued: "Queued. This runs in the background — you can leave this page.",
  running: "Importing…",
  completed: "Import finished.",
  failed: "The import failed.",

  /** ------------------------------------------------------------------ */
  /** History + undo (D13)                                                 */
  /** ------------------------------------------------------------------ */

  historyTitle: "Past imports",
  historyEmpty: "Nothing has been imported yet.",
  undoAction: "Undo this import",
  undoConfirmTitle: "Undo this import?",
  /**
   * 🔴 DELIBERATELY not `undoAction`. The confirmation is portalled over a
   * table whose rows still carry their own undo buttons, so reusing the label
   * puts several identically-named buttons on the page at once — assistive
   * technology then announces "Undo this import" for both the thing that opens
   * the dialog and the thing that commits it, with no way to tell them apart.
   */
  undoConfirmAction: "Undo it",
  undoRunning: "Undoing…",
  undoneLabel: "Undone",
  undoKeptTitle: "Buyers this undo kept",
  undoKeptHint:
    "Somebody has worked these since the import, so they were left alone rather than archived.",
  undoArchived: "Archived",
  undoKept: "Kept",
} as const;

/**
 * How the review screen labels where the plan came from.
 *
 * <p>The `reused` case NAMES THE DATE, because "using the mapping from a
 * previous import" without saying which one is an assurance the operator
 * cannot check. An origin arriving without its date still says something true
 * rather than rendering "the import on ".</p>
 */
export function planOriginLabel(
  origin: BuyerImportPlanOrigin,
  reusedFromBatchAt: string | null | undefined,
): string {
  switch (origin) {
    case "exact-match":
      return smartImportCopy.originExactMatch;
    case "reused":
      return reusedFromBatchAt
        ? `using the mapping from the import on ${formatCalendarDate(reusedFromBatchAt)}`
        : "using the mapping from a previous import";
    case "manual-fallback":
      return smartImportCopy.originManualFallback;
    default:
      return smartImportCopy.originAi;
  }
}

/**
 * The AI-fallback banner (D19).
 *
 * <p>The plan is still usable — that is the point of saying this at all. A
 * banner that only reported the failure would read as "start again", when what
 * actually happened is that exact-name matches landed and everything else went
 * to Notes for the operator to re-point.</p>
 */
export function aiFallbackBanner(message: string): string {
  return `${message} Every column that matched a known name has been mapped; the rest are on Notes, and you can re-point any of them below.`;
}

export const PREVIEW_CHIPS: readonly {
  id: BuyerSmartImportPreviewChip;
  label: string;
}[] = [
  { id: "all", label: "All" },
  { id: "merged", label: "Merged" },
  { id: "flagged", label: "Flagged" },
  { id: "rejected", label: "Rejected" },
];
